import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Search as MagnifyingGlass, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { queueCase } from "@/lib/offlineQueue";

const BAND_CLASS = { low: "risk-low", medium: "risk-medium", high: "risk-high", critical: "risk-critical" };
const STATUS_CLASS = { open: "status-open", in_progress: "status-in_progress", referred: "status-referred", closed: "status-closed" };

export default function Cases() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const [status, setStatus] = useState("");
  const [showNew, setShowNew] = useState(false);

  const canCreate = user && user.role !== "donor";

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (band) params.band = band;
    if (status) params.status = status;
    const { data } = await api.get("/cases", { params });
    setCases(data);
  };
  useEffect(() => { load(); }, [q, band, status]);

  return (
    <div className="p-8 md:p-10 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline">Registry</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mt-2">Cases</h1>
        </div>
        {canCreate && (
          <button data-testid="new-case-btn" onClick={() => setShowNew(true)}
            className="bg-[#0033A0] text-white px-5 py-3 flex items-center gap-2 hover:bg-slate-900 transition-colors">
            <Plus size={16} strokeWidth={2.5} /> New case
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="border border-slate-200 grid grid-cols-1 md:grid-cols-4">
        <div className="p-4 border-r border-slate-200 flex items-center gap-2">
          <MagnifyingGlass size={16} className="text-slate-400" />
          <input data-testid="cases-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search ID, name, location"
            className="w-full bg-transparent focus:outline-none text-sm" />
        </div>
        <select data-testid="filter-band" value={band} onChange={e => setBand(e.target.value)}
          className="p-4 border-r border-slate-200 text-sm bg-white focus:outline-none">
          <option value="">All risk bands</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select data-testid="filter-status" value={status} onChange={e => setStatus(e.target.value)}
          className="p-4 border-r border-slate-200 text-sm bg-white focus:outline-none">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="referred">Referred</option>
          <option value="closed">Closed</option>
        </select>
        <div className="p-4 text-sm text-slate-500 flex items-center font-mono">
          {cases.length} record{cases.length !== 1 && "s"}
        </div>
      </div>

      {/* Table */}
      <div className="border border-t-0 border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left p-4 overline">Case ID</th>
              <th className="text-left p-4 overline">Subject</th>
              <th className="text-left p-4 overline">Category</th>
              <th className="text-left p-4 overline">Risk</th>
              <th className="text-left p-4 overline">Status</th>
              <th className="text-left p-4 overline">Created</th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">No cases yet.</td></tr>
            )}
            {cases.map(c => (
              <tr key={c.id} data-testid={`case-row-${c.case_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-4 font-mono text-xs">
                  <Link to={`/cases/${c.case_id}`} className="text-[#0033A0] font-semibold underline underline-offset-2">
                    {c.case_id}
                  </Link>
                </td>
                <td className="p-4">
                  <div className="font-semibold">{c.subject_name}</div>
                  <div className="text-xs text-slate-500">{c.location || "—"}</div>
                </td>
                <td className="p-4 capitalize">{c.category.replace("_"," ")}</td>
                <td className="p-4">
                  <span className={`${BAND_CLASS[c.risk_band]} px-2 py-1 text-xs font-bold uppercase tracking-wider`}>
                    {c.risk_band} · {c.risk_score}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`${STATUS_CLASS[c.status]} px-2 py-1 text-xs font-bold uppercase tracking-wider`}>
                    {c.status.replace("_"," ")}
                  </span>
                </td>
                <td className="p-4 text-xs text-slate-500 font-mono">{c.created_at?.slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewCaseModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewCaseModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    subject_name: "", age: "", gender: "", location: "",
    category: "child_protection", description: "", severity: "medium",
    has_disability: false, out_of_school: false, abuse_reported: false, household_income: "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const payload = { ...form };
      payload.age = payload.age ? Number(payload.age) : null;
      if (!payload.household_income) delete payload.household_income;
      if (!navigator.onLine) {
        await queueCase(payload);
        onCreated();
        return;
      }
      await api.post("/cases", payload);
      onCreated();
    } catch (e) {
      // If network failure, queue it
      if (!navigator.onLine || e.message === "Network Error") {
        try { await queueCase({ ...form, age: form.age ? Number(form.age) : null }); onCreated(); return; } catch {}
      }
      setErr(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl border border-slate-300 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <div className="overline">Intake</div>
            <h3 className="font-heading font-extrabold text-2xl tracking-tight">New case</h3>
          </div>
          <button onClick={onClose} data-testid="close-newcase" className="p-2 hover:bg-slate-100"><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="overline block mb-1">Subject name</label>
            <input data-testid="case-subject" required value={form.subject_name} onChange={e => setForm({...form, subject_name: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          <div><label className="overline block mb-1">Age</label>
            <input data-testid="case-age" type="number" min="0" value={form.age} onChange={e => setForm({...form, age: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          <div><label className="overline block mb-1">Gender</label>
            <select value={form.gender} onChange={e => setForm({...form, gender: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="">—</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option>
            </select>
          </div>
          <div className="md:col-span-2"><label className="overline block mb-1">Location</label>
            <input data-testid="case-location" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          <div><label className="overline block mb-1">Category</label>
            <select data-testid="case-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="child_protection">Child Protection</option>
              <option value="education">Education</option>
              <option value="health">Health</option>
              <option value="livelihood">Livelihood</option>
              <option value="legal">Legal</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div><label className="overline block mb-1">Severity</label>
            <select data-testid="case-severity" value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
            </select>
          </div>
          <div><label className="overline block mb-1">Household income</label>
            <select value={form.household_income} onChange={e => setForm({...form, household_income: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="">—</option><option value="none">None</option><option value="below_poverty">Below poverty</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </div>
          <div className="md:col-span-2 grid grid-cols-3 gap-3">
            {[
              ["has_disability", "Has disability"],
              ["out_of_school", "Out of school"],
              ["abuse_reported", "Abuse reported"],
            ].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 border border-slate-200 p-3 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={form[k]} onChange={e => setForm({...form, [k]: e.target.checked})} />
                <span>{l}</span>
              </label>
            ))}
          </div>
          <div className="md:col-span-2"><label className="overline block mb-1">Description</label>
            <textarea rows="3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          {err && <div className="md:col-span-2 border border-red-300 bg-red-50 text-red-700 text-sm px-4 py-3">{String(err)}</div>}
          <div className="md:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={onClose} className="border border-slate-300 px-5 py-3 hover:bg-slate-100">Cancel</button>
            <button data-testid="submit-case" disabled={saving} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900">
              {saving ? "Saving…" : "Register case"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
